import * as T from '../types';
import EventNames from '../../../config/eventNames';
import { ManifestType } from '@clientos/types/dist';

/**
 * Handles route-specific manifest enhancements
 * Processes native routes and block navigation configurations
 */
export class ManifestRouteEnhancer {
  /**
   * Processes navigation routes in the manifest
   * Separates native routes from web routes and configures block navigation
   */
  static enhanceNativeRoutes(manifest: ManifestType): void {
    // Ensure navigation and routes always exist before any processing
    this.ensureDefaultRoutes(manifest);

    const nativeRoutes: T.NavigationServiceInputType['blockNavigation']['pathList'] =
      [];

    manifest.navigation.routes = this.processRoutes(
      manifest.navigation.routes,
      nativeRoutes,
      false
    );

    if (nativeRoutes.length > 0) {
      this.configureBlockNavigation(manifest, nativeRoutes);
    }
  }

  /**
   * Ensures the manifest always has a navigation object with at least
   * a default home route. This guarantees consumers reading directly
   * from the manifest state (e.g. manifest.navigation?.routes) always
   * get a valid structure without requiring changes on their side.
   */
  private static ensureDefaultRoutes(manifest: ManifestType): void {
    if (!manifest.navigation) manifest.navigation = {};

    const routes = manifest.navigation.routes;
    if (!Array.isArray(routes) || routes.length === 0) {
      manifest.navigation.routes = [
        {
          key: 'home',
          label: 'Home',
          translationKey: 'route.home',
          iconReference: 'IconHome',
          path: '/',
          assetReference: '_MISSING_CUSTOM_ROUTE_CONFIGURATION'
        }
      ];
    }
  }

  /**
   * Recursively processes routes to separate native from web routes
   */
  private static processRoutes(
    routes: any[],
    nativeRoutes: any[],
    isSubRoute: boolean
  ): any[] {
    return routes.filter((route) => {
      if (route?.isNative) {
        return this.handleNativeRoute(route, nativeRoutes, isSubRoute);
      } else {
        return this.handleWebRoute(route, nativeRoutes);
      }
    });
  }

  /**
   * Handles native route processing and validation
   */
  private static handleNativeRoute(
    route: any,
    nativeRoutes: any[],
    isSubRoute: boolean
  ): boolean {
    if (isSubRoute) {
      console.error(
        `Manifest error: route path (${route?.path}) is native and cannot be used as subRoute.`
      );
      return false;
    }

    if (!this.validateNativeRoute(route)) {
      return false;
    }

    nativeRoutes.push({
      path: route?.path,
      exact: route?.exact,
      eventName: route?.eventName,
      serviceId: route?.serviceId,
      properties: route?.properties
    });

    return false; // Native routes are filtered out of web routes
  }

  /**
   * Handles web route processing
   */
  private static handleWebRoute(route: any, nativeRoutes: any[]): boolean {
    if (route?.subRoutes) {
      route.subRoutes = this.processRoutes(route.subRoutes, nativeRoutes, true);
      if (route.subRoutes.length === 0) {
        delete route.subRoutes;
      }
    }
    return true;
  }

  /**
   * Validates native route configuration
   */
  private static validateNativeRoute(route: any): boolean {
    if (!route?.path) {
      console.error('Manifest error: route.path is required for native routes');
      return false;
    }

    if (!route?.eventName && !route?.serviceId) {
      console.error(
        `Manifest error: native route (${route.path}) does not have eventName or serviceId.`
      );
      return false;
    }

    return true;
  }

  /**
   * Configures block navigation with native routes
   */
  private static configureBlockNavigation(
    manifest: any,
    nativeRoutes: any[]
  ): void {
    // Process existing block navigation paths
    if (manifest.services?.navigation?.blockNavigation?.pathList?.length) {
      manifest.services.navigation.blockNavigation.pathList =
        this.processExistingBlockNavigation(
          manifest.services.navigation.blockNavigation.pathList
        );
    }

    // Add native routes to block navigation
    manifest.services = {
      ...(manifest.services || {}),
      navigation: {
        ...(manifest.services?.navigation || {}),
        blockNavigation: {
          ...(manifest.services?.navigation?.blockNavigation || {}),
          pathList: [
            ...(manifest.services?.navigation?.blockNavigation?.pathList || []),
            ...nativeRoutes
          ]
        }
      }
    };
  }

  /**
   * Processes existing block navigation paths
   */
  private static processExistingBlockNavigation(
    pathList: (
      | T.NavigationServiceInputBlockNavigationPathListObjectType
      | string
    )[]
  ): T.NavigationServiceInputBlockNavigationPathListObjectType[] {
    return pathList
      .map(
        (
          value
        ):
          | T.NavigationServiceInputBlockNavigationPathListObjectType
          | undefined => {
          const eventName = EventNames.clientosNavigationBlocked;

          if (typeof value === 'string') {
            return { path: value, eventName };
          } else {
            if (!value?.path) {
              console.error(
                'Manifest error: blockNavigation.pathList.path is required'
              );
              return undefined;
            }
            return {
              eventName: eventName,
              ...value
            };
          }
        }
      )
      .filter(
        (
          item
        ): item is T.NavigationServiceInputBlockNavigationPathListObjectType =>
          item !== undefined
      );
  }
}
